import React from "react";
import { useSelector } from "react-redux";
import { selectCateringCartFilters, selectCateringCartPackages } from "../../redux/features/restaurants/restaurantSelectors";

const Categories = ({ categories, activeCategory, setActiveCategory, disabled = false, isCatering = false, packageData = null }) => {
  const filters = useSelector(selectCateringCartFilters);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  
  // Get the same package data that CateringOrderSummary uses for validation
  const activePackage = cateringCartPackages && cateringCartPackages.length > 0 ? cateringCartPackages[0] : packageData;
  
  
  // Helper function to get requirements for a category based on current PAX
  const getCategoryRequirements = (category) => {
    if (!isCatering) {
      return category.items; // Fallback to original items display for non-catering
    }
    
    // Use the same data source as CateringOrderSummary validation
    const packageToCheck = activePackage;
    if (!packageToCheck || !packageToCheck.categories) {
      return category.items; // Fallback to original items display
    }
    
    const currentPax = filters.pax || 20;
    
    // Find the category in package data that has selection_rules
    const packageCategory = packageToCheck.categories.find(pkgCat => pkgCat.id === category.id);
    
    if (!packageCategory) {
      return category.items; // Fallback to original items display
    }
    
    // Support both choose_rule and selection_rules naming conventions (same as validation function)
    const rules = packageCategory.choose_rule || packageCategory.selection_rules;
    if (!rules || !Array.isArray(rules)) {
      return category.items; // Fallback to original items display
    }
    
    let matchingRule = null;
    let requiredItemsForCategory = 0;

    // Handle different rule structures (same logic as validatePackageCompletion)
    if (packageCategory.selection_rules) {
      // New structure with pax_range and max_selections
      const sortedRules = [...rules].sort((a, b) => 
        parseInt(b.pax_range?.min || 0) - parseInt(a.pax_range?.min || 0)
      );
      matchingRule = sortedRules.find(rule => 
        currentPax >= parseInt(rule.pax_range?.min || 0) && 
        currentPax <= parseInt(rule.pax_range?.max || 999)
      );
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.max_selections || 0) : 0;
    } else {
      // Old structure with pax and choose
      const sortedRules = [...rules].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      matchingRule = sortedRules.find(rule => 
        currentPax >= parseInt(rule.pax)
      );
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.choose || 0) : 0;
    }
    
    if (requiredItemsForCategory > 0) {
      return `${requiredItemsForCategory} required`;
    }
    
    return category.items || '0 items';
  };

  // Helper function to get current selections count for a category
  const getCurrentSelections = (category) => {
    if (!isCatering || !cateringCartPackages || cateringCartPackages.length === 0) {
      return 0;
    }
    
    // Get items from the first (and only) package in cart
    const currentPackage = cateringCartPackages[0];
    if (!currentPackage || !currentPackage.items) {
      return 0;
    }
    
    // Count items in this category
    const itemsInCategory = currentPackage.items.filter(item => 
      item.category_id === category.id
    );
    
    // Sum up quantities (including variant items)
    return itemsInCategory.reduce((total, item) => {
      if (item.has_variants && item.items) {
        return total + item.items.reduce((variantTotal, variantItem) => 
          variantTotal + (variantItem.quantity || 0), 0
        );
      }
      return total + (item.quantity || 0);
    }, 0);
  };

  return (
    <div className="flex flex-wrap sm:justify-start justify-center gap-4 pb-2">
      {categories?.map((category) => {
        const requirements = getCategoryRequirements(category);
        const currentSelections = getCurrentSelections(category);
        // Show progress if we have package data with selection rules for this category (use same data source as validation)
        const packageToCheck = activePackage;
        const packageCategory = packageToCheck?.categories?.find(pkgCat => pkgCat.id === category.id);
        const hasRules = packageCategory && (packageCategory.selection_rules || packageCategory.choose_rule);
        const showProgress = isCatering && hasRules;
        
        return (
          <button
            key={category.id}
            className={`flex items-center p-4 w-[156px] h-[78px] text-xs text-gray-50 rounded-xl font-bold ${
              activeCategory === category.id
                ? "bg-white border-2 border-amber-100"
                : "bg-white border-2 border-gray-150"
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !disabled && setActiveCategory(category.id)}
            disabled={disabled}
          >
            {/* Text Section */}
            <div className="text-left w-full">
              <div className="font-bold text-gray-800 mb-1">
                {category.name}
              </div>
              <div className="text-xs">
                {showProgress ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-gray-600">
                      {currentSelections}/{requirements.replace(' required', '')} selected
                    </span>
                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          currentSelections >= parseInt(requirements.replace(' required', '')) 
                            ? 'bg-green-500' 
                            : 'bg-amber-400'
                        }`}
                        style={{ 
                          width: `${Math.min(100, (currentSelections / parseInt(requirements.replace(' required', '') || 1)) * 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-600">{requirements}</span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default Categories;
